import { CartLineItem } from 'models/Cart';
import useSWR from 'swr';
import { getNetPrice } from 'utils/utils';
import httpApi from '../api/httpApi';

const PRICE_LIST_URL = '/api/v2/prices';

type VariantID = string;

type ProductID = string;

export interface CustomPrice {
    net_price: number;
    min_quantity: number;
}

export interface GetCustomPricesResponse {
    [key: ProductID]: {
        [key: VariantID]: CustomPrice[];
    };
}

const useProductsPriceSWR = (
    productIds?: number[]
): {
    prices: GetCustomPricesResponse;
    isLoading: boolean;
    isError: boolean;
    extractCustomPrices: (
        response: GetCustomPricesResponse,
        productID: string,
        variantID: string
    ) => CustomPrice[];
    getMinCustomPrice: (
        customs: CustomPrice[],
        quantity: number
    ) => CustomPrice;
    getMinNetPrice: (item: CartLineItem) => number;
    // forceRefresh: KeyedMutator<any[]>;
} => {
    const fetch = async (
        query,
        productIds: number[]
    ): Promise<GetCustomPricesResponse> => {
        if (productIds.length > 0) {
            return await httpApi
                .post<GetCustomPricesResponse>(query, {
                    productIds,
                })
                .then((res) => {
                    return res.getData();
                })
                .catch((error) => {
                    throw error;
                });
        } else {
            return {} as GetCustomPricesResponse;
        }
    };

    const { data, error, isLoading, mutate } = useSWR(
        [PRICE_LIST_URL, productIds],
        ([url, productIds]) => fetch(url, productIds),
        {
            dedupingInterval: 600000, // 10min
            errorRetryCount: 3,
        }
    );

    const extractCustomPrices = (
        response: GetCustomPricesResponse,
        productID: string,
        variantID: string
    ): CustomPrice[] => {
        if (!response || Object.keys(response).length === 0) return null;

        if (!response[productID]) return null;

        return response[productID][variantID];
    };

    const getMinCustomPrice = (
        customs: CustomPrice[],
        quantity: number
    ): CustomPrice => {
        return customs?.reduce(
            (min: CustomPrice, current: CustomPrice): CustomPrice => {
                if (
                    current.min_quantity > quantity ||
                    (!!min && min.min_quantity > current.min_quantity)
                )
                    return min;
                return current;
            },
            null
        );
    };

    const getMinNetPrice = (item: CartLineItem): number => {
        const minCustomPrice = getMinCustomPrice(item.customs, item.quantity);

        if (!minCustomPrice) {
            return getNetPrice(+item.cost.amountPerQuantity.amount);
        }

        return minCustomPrice.net_price;
    };

    return {
        prices: data ?? null,
        isLoading: isLoading,
        isError: error,
        extractCustomPrices,
        getMinNetPrice,
        getMinCustomPrice,
        // forceRefresh: mutate,
    };
};

export default useProductsPriceSWR;
